/**
 * @file CookiesProvider.tsx
 * @description
 * -----------------------------------------------------
 * Proveedor de cookies
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-10-01
 * @author DLTCode
 * @author Nacho Moya
 * @copyright © DLTCode
 * @license Private
 * */

"use client";

import { c as _c } from "react/compiler-runtime";
import { acceptAllCookies as acceptAllCookiesAsync, acceptRequiredOnly as acceptRequiredOnlyAsync, getCookiePreferences, type CookiePreferences } from "@/lib/cookies";
import { createContext, useCallback, useContext, useEffect, useState, useTransition } from "react";
interface CookiesContextType {
  cookiePreferences: CookiePreferences | null;
  areCookiesAccepted: boolean;
  isLoading: boolean;
  acceptAllCookies: () => void;
  acceptRequiredOnly: () => void;
  isPending: boolean;
  showCustomizeCookies: boolean;
  setShowCustomizeCookies: (_show: boolean) => void;
  showStrictCookiesDescription: boolean;
  setShowStrictCookiesDescription: (_show: boolean) => void;
}
interface AcceptCookiesProviderProps {
  children: React.ReactNode;
}
const CookiesContext = createContext<CookiesContextType>({
  cookiePreferences: null,
  areCookiesAccepted: false,
  isLoading: true,
  acceptAllCookies: () => {},
  acceptRequiredOnly: () => {},
  isPending: false,
  showCustomizeCookies: false,
  setShowCustomizeCookies: () => {},
  showStrictCookiesDescription: false,
  setShowStrictCookiesDescription: () => {}
});
export const CookiesProvider = t0 => {
  const $ = _c(14);
  const {
    children
  } = t0;
  const [isPending, startTransition] = useTransition();
  const [cookiePreferences, setCookiePreferences] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showCustomizeCookies, setShowCustomizeCookies] = useState(false);
  const [showStrictCookiesDescription, setShowStrictCookiesDescription] = useState(false);
  const areCookiesAccepted = cookiePreferences !== null;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      (async () => {
        const prefs = await getCookiePreferences();
        setCookiePreferences(prefs);
        setIsLoading(false);
      })();
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      startTransition(async () => {
        await acceptAllCookiesAsync();
        setCookiePreferences({
          required: true,
          analytics: true,
          timestamp: Date.now()
        });
      });
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const acceptAllCookies = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      startTransition(async () => {
        await acceptRequiredOnlyAsync();
        setCookiePreferences({
          required: true,
          analytics: false,
          timestamp: Date.now()
        });
      });
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const acceptRequiredOnly = t4;
  let t5;
  if ($[4] !== areCookiesAccepted || $[5] !== cookiePreferences || $[6] !== isLoading || $[7] !== isPending || $[8] !== showCustomizeCookies || $[9] !== showStrictCookiesDescription) {
    t5 = {
      cookiePreferences,
      areCookiesAccepted,
      isLoading,
      acceptAllCookies,
      acceptRequiredOnly,
      isPending,
      showCustomizeCookies,
      setShowCustomizeCookies,
      showStrictCookiesDescription,
      setShowStrictCookiesDescription
    };
    $[4] = areCookiesAccepted;
    $[5] = cookiePreferences;
    $[6] = isLoading;
    $[7] = isPending;
    $[8] = showCustomizeCookies;
    $[9] = showStrictCookiesDescription;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== children || $[12] !== t5) {
    t6 = <CookiesContext value={t5}>{children}</CookiesContext>;
    $[11] = children;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
};
export const useAcceptCookies = () => {
  return useContext(CookiesContext);
};